import type { GifEncoderAdapter } from '../encoders/GifEncoderAdapter'
import type { ExportCompleteCallback, ExportProgressCallback } from '../encoders/GifEncoderAdapter'
import type { FrameRenderer } from '../FrameRenderer'
import type { TimelineFrame } from '../TimelineSampler'
import type { ExportResult, NormalizedGifExportConfig } from '../types'
import type { EffectsState } from '../../effects/types'
import type { FullRenderSettings } from '../../types'
import { generateFilename } from '../types'

export interface GifExportJobDependencies {
  frameRenderer: FrameRenderer
  encoder: GifEncoderAdapter
  timeline: TimelineFrame[]
}

export interface GifExportJobInput {
  sourceImage: HTMLImageElement | HTMLCanvasElement | ImageData
  settings?: Partial<FullRenderSettings>
  effectsState?: EffectsState
}

export class GifExportJob {
  private progressCallback?: ExportProgressCallback
  private completeCallback?: ExportCompleteCallback
  private cancelled = false

  constructor(
    private readonly config: NormalizedGifExportConfig,
    private readonly input: GifExportJobInput,
    private readonly dependencies: GifExportJobDependencies
  ) {}

  onProgress(callback: ExportProgressCallback): void {
    this.progressCallback = callback
  }

  onComplete(callback: ExportCompleteCallback): void {
    this.completeCallback = callback
  }

  async start(): Promise<void> {
    await this.dependencies.encoder.start(this.config)

    this.progressCallback?.({
      status: 'preparing',
      progress: 0,
      totalFrames: this.config.totalFrames,
      message: 'Preparing offline GIF export...',
    })

    for (const frame of this.dependencies.timeline) {
      if (this.cancelled) {
        return
      }

      const { canvas } = this.dependencies.frameRenderer.renderFrame({
        sourceImage: this.input.sourceImage,
        settings: this.input.settings,
        effectsState: this.input.effectsState,
        width: this.config.width,
        height: this.config.height,
        frameIndex: frame.frameIndex,
        totalFrames: this.config.totalFrames,
        timeMs: frame.timeMs,
        fitMode: this.config.fitMode,
      })

      await this.dependencies.encoder.addFrame(canvas, frame.delayMs)

      const renderedFrames = frame.frameIndex + 1
      this.progressCallback?.({
        status: 'rendering_frames',
        progress: Math.round((renderedFrames / this.config.totalFrames) * 80),
        currentFrame: renderedFrames,
        totalFrames: this.config.totalFrames,
        message: `Rendering GIF frames ${renderedFrames}/${this.config.totalFrames}`,
      })
    }

    this.progressCallback?.({
      status: 'encoding',
      progress: 80,
      currentFrame: this.config.totalFrames,
      totalFrames: this.config.totalFrames,
      message: 'Encoding GIF...',
    })

    try {
      const blob = await this.dependencies.encoder.finish()
      const result: ExportResult = {
        success: true,
        filename: generateFilename('gif'),
        size: blob.size,
        blob,
        timestamp: Date.now(),
      }

      this.progressCallback?.({
        status: 'completed',
        progress: 100,
        currentFrame: this.config.totalFrames,
        totalFrames: this.config.totalFrames,
        message: 'GIF export completed',
      })
      this.complete(result)
    } catch (error) {
      const result: ExportResult = {
        success: false,
        filename: generateFilename('gif'),
        size: 0,
        error: error instanceof Error ? error.message : 'GIF export failed',
        timestamp: Date.now(),
      }
      this.progressCallback?.({
        status: this.cancelled ? 'cancelled' : 'error',
        progress: 0,
        currentFrame: this.config.totalFrames,
        totalFrames: this.config.totalFrames,
        message: result.error,
      })
      this.complete(result)
    }
  }

  async cancel(): Promise<void> {
    this.cancelled = true
    await this.dependencies.encoder.cancel()
    this.progressCallback?.({
      status: 'cancelled',
      progress: 0,
      message: 'GIF export cancelled.',
    })
  }

  isCancelled(): boolean {
    return this.cancelled
  }

  protected complete(result: ExportResult): void {
    this.completeCallback?.(result)
  }
}
